import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown, X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface SearchableComboboxProps {
    value: string;
    onChange: (value: string) => void;
    options: string[];
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    allowCreate?: boolean;
    onCreateNew?: (value: string) => void;
}

export function SearchableCombobox({
    value,
    onChange,
    options,
    placeholder = "Select…",
    disabled = false,
    className,
    allowCreate = false,
    onCreateNew,
}: SearchableComboboxProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const filtered = options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
    const showCreate =
        allowCreate && search.trim() && !options.some((o) => o.toLowerCase() === search.trim().toLowerCase());

    const handleSelect = useCallback(
        (val: string) => {
            onChange(val);
            setSearch("");
            setOpen(false);
        },
        [onChange],
    );

    const handleClear = useCallback(() => {
        onChange("");
        setSearch("");
        setOpen(false);
    }, [onChange]);

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch("");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (open && inputRef.current) {
            inputRef.current.focus();
        }
    }, [open]);

    return (
        <div ref={containerRef} className={cn("relative", className)}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen(!open)}
                className={cn(
                    "flex items-center w-full h-12 px-3 text-left border rounded-md bg-background",
                    "text-sm transition-colors",
                    disabled ? "opacity-50 cursor-not-allowed bg-muted" : "hover:border-primary/50",
                    open && "ring-2 ring-ring ring-offset-1",
                )}
            >
                <span className={cn("flex-1 truncate", !value && "text-foreground/80")}>{value || placeholder}</span>
                {value && !disabled ? (
                    <X
                        className="h-4 w-4 text-foreground/80 shrink-0 ml-1"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClear();
                        }}
                    />
                ) : (
                    <ChevronDown className="h-4 w-4 text-foreground/80 shrink-0 ml-1" />
                )}
            </button>

            {open && (
                <div
                    ref={listRef}
                    className="absolute z-[9999] left-0 right-0 mt-1 max-h-52 overflow-auto rounded-md border bg-popover shadow-lg"
                >
                    <div className="sticky top-0 bg-popover p-1.5 border-b">
                        <input
                            ref={inputRef}
                            type="text"
                            className="w-full h-9 px-2 text-sm border rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                            placeholder="Type to search…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (filtered.length === 1) {
                                        handleSelect(filtered[0]);
                                    } else if (showCreate && onCreateNew) {
                                        onCreateNew(search.trim());
                                        setSearch("");
                                        setOpen(false);
                                    }
                                }
                                if (e.key === "Escape") {
                                    setOpen(false);
                                    setSearch("");
                                }
                            }}
                        />
                    </div>
                    {filtered.length === 0 && !showCreate && (
                        <div className="px-3 py-4 text-sm text-foreground/80 text-center">No results</div>
                    )}
                    {filtered.map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            className={cn(
                                "w-full text-left px-3 py-2.5 text-sm hover:bg-accent transition-colors",
                                opt === value && "bg-accent font-medium",
                            )}
                            onClick={() => handleSelect(opt)}
                        >
                            {opt}
                        </button>
                    ))}
                    {showCreate && (
                        <button
                            type="button"
                            className="w-full text-left px-3 py-2.5 text-sm hover:bg-accent transition-colors flex items-center gap-1.5 text-primary font-medium border-t"
                            onClick={() => {
                                if (onCreateNew) {
                                    onCreateNew(search.trim());
                                } else {
                                    handleSelect(search.trim());
                                }
                                setSearch("");
                                setOpen(false);
                            }}
                        >
                            <Plus className="h-3.5 w-3.5" />
                            Add "{search.trim()}"
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
